/** Day-Plan Template admin types — mirror fsm-oms-backend
 *  src/modules/business/epc/epcDayPlanTemplate interfaces (toJSON maps _id→id). */

export type TemplateDayType = 'W' | 'G' | 'Z' | 'M';
export type TemplateMode = 'cycle' | 'fixedSequence';
export type TemplateStatus = 'draft' | 'published' | 'archived';
export type QtyKind = 'discrete' | 'continuous';

export interface TemplateGateRef {
    kind: 'education' | 'safety' | 'qaqc';
    itemId?: string;
    workTypeKey?: string;
}

export interface TemplateStep {
    order: number;
    durationDays: number;
    dayType: TemplateDayType;
    elapsed: boolean;
    description: string;
    workInstructions?: string;
    qtyShare: number;
    manpower: { roleLabel: string; skillLevel: 'skilled' | 'unskilled'; count: number }[];
    machinery: { name: string; count: number }[];
    materials: { name: string; qtyPerUnit?: number; unit?: string }[];
    gateRefs: TemplateGateRef[];
    zReasonCategory?: string;
}

export interface DayPlanTemplate {
    id: string;
    name: string;
    description?: string;
    disciplineKey: string | null;
    disciplineLabel: string | null;
    mode: TemplateMode;
    unit: string;
    qtyKind: QtyKind;
    qtyPerCycle: number;
    nominalRate?: number;
    matchTokens: string[];
    workTypeKey?: string;
    status: TemplateStatus;
    latestVersion: number;
    draftSteps: TemplateStep[];
    active: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface DayPlanTemplateVersion {
    id: string;
    templateId: string;
    version: number;
    name: string;
    mode: TemplateMode;
    unit: string;
    qtyKind: QtyKind;
    qtyPerCycle: number;
    nominalRate?: number;
    steps: TemplateStep[];
    cycleDaysPerUnit: number;
    cycleSpanWorkdays: number;
    publishedAt: string;
    changeNote?: string;
}

export interface QtyUnit {
    id: string;
    key: string;
    label: string;
    qtyKind: QtyKind;
    synonyms: string[];
    active: boolean;
}

export type UpsertTemplateInput = Omit<
    DayPlanTemplate,
    'id' | 'status' | 'latestVersion' | 'active' | 'createdAt' | 'updatedAt'
>;

// ── Phase 4: mapping / suggestions / generation ────────────────────────────

export interface MappingParams {
    totalQty: number;
    unit: string;
    gangs: number;
    gangLabels?: string[];
    skipPastDays: boolean;
}

export interface TaskTemplateMapping {
    id: string;
    epcProjectId: string;
    orgUnitId: string;
    taskKey: string;
    templateId: string;
    templateVersion: number;
    params: MappingParams;
    status: 'mapped' | 'generated';
    drift?: {
        reason: 'schedule_reimport' | 'template_version';
        detectedAt: string;
        newTemplateVersion?: number;
    };
    reviewFlags: {
        date: string;
        reason: 'edited' | 'has_actuals' | 'outside_new_window' | 'past';
        activityIds: string[];
    }[];
    lastGenerationId?: string;
    lastGeneratedAt?: string;
}

export interface TemplateSuggestionsResponse {
    task: {
        taskKey: string;
        name: string;
        start: string;
        finish: string;
        durationDays: number;
        workDays: number;
        orgUnitId: string | null;
        disciplineKey: string | null;
        isSummary: boolean;
    };
    qtyPrefill: { qty: number; unit: string } | null;
    suggestions: {
        templateId: string;
        name: string;
        latestVersion: number;
        unit: string;
        nominalRate?: number;
        mode: TemplateMode;
        score: number;
        matchedTokens: string[];
        solverPrefill: { requiredGangs: number; impliedRate: number } | null;
    }[];
}

export interface GenerationPreview {
    generationId: string;
    solver: {
        workDays: number;
        cycleDaysPerUnit: number;
        numCycles: number;
        requiredGangs: number;
        chosenGangs: number;
        impliedRate: number;
        nominalRate?: number;
        rateWarning?: string;
        fit: 'exact' | 'stretched' | 'compressed';
        naturalEndDate: string;
        taskFinish: string;
    };
    summary: {
        workActivityCount: number;
        gateActivityCount: number;
        zeroActivityCount: number;
        handoverActivityCount: number;
        activityCount: number;
        qtyCheck: { totalPlanned: number; totalQty: number; exact: boolean };
    };
    perDay: {
        date: string;
        counts: Record<TemplateDayType, number>;
        plannedQty: number;
        gangs: string[];
    }[];
    collisions: { date: string; manualCount: number; manualActivityIds: string[] }[];
    warnings: string[];
}

export interface PutMappingInput {
    templateId: string;
    templateVersion?: number;
    params: {
        totalQty: number;
        unit: string;
        gangs?: number;
        gangLabels?: string[];
        skipPastDays?: boolean;
    };
}

export interface GenerateResult {
    inserted: number;
    replaced: number;
    keptFlagged: number;
    mapping: TaskTemplateMapping;
}

// ── Phase 5: regeneration ───────────────────────────────────────────────────

export type RegenVerdict =
    | 'replace'
    | 'keep_edited'
    | 'keep_actuals'
    | 'keep_past'
    | 'orphan_removed'
    | 'orphan_kept'
    | 'new_tail'
    | 'unchanged';

export interface RegenerationDiff {
    generationId: string;
    trigger: 'schedule_reimport' | 'template_version' | 'manual';
    acceptedTemplateVersion?: number;
    window: { oldStart: string | null; oldFinish: string | null; newStart: string; newFinish: string };
    days: {
        date: string;
        verdict: RegenVerdict;
        current?: { activityCount: number; plannedQty: number; edited: boolean; hasActuals: boolean };
        proposed?: { activityCount: number; plannedQty: number };
    }[];
    totals: {
        replace: number;
        keptEdited: number;
        keptActuals: number;
        keptPast: number;
        orphanRemoved: number;
        orphanKept: number;
        newTail: number;
        unchanged: number;
    };
    solver: GenerationPreview['solver'];
    warnings: string[];
}

export interface RegenerateResult extends GenerateResult {
    diff: RegenerationDiff;
}

// ── Bulk template auto-map (Feature A, gap-closers spec §2) ──────────────────

export type BulkMatchBand = 'high' | 'medium' | 'none';

export interface BulkMatchSuggestion {
    templateId: string;
    name: string;
    templateVersion: number;
    unit: string;
    score: number;
    matchedTokens: string[];
}

export interface BulkMatchRow {
    taskKey: string;
    taskName: string;
    orgUnitName?: string;
    suggestions: BulkMatchSuggestion[];
    band: BulkMatchBand;
    qtyPrefill: { qty: number; unit: string } | null;
    gangsPrefill: number | null;
    existingMapping: boolean;
    blocks: string[];
}

export interface BulkMatchPreview {
    bulkMatchId: string;
    importId: string;
    snapshotId: string;
    rows: BulkMatchRow[];
    unmatchedCount: number;
}

export interface BulkMatchCommitRow {
    taskKey: string;
    accept: boolean;
    templateId: string;
    totalQty: number;
    gangs?: number;
}

export interface BulkMatchCommitBody {
    bulkMatchId: string;
    importId?: string;
    rows: BulkMatchCommitRow[];
}

export interface BulkMatchCommitResult {
    created: number;
    skipped: number;
    failed: { taskKey: string; error: string }[];
    unmatchedCount: number;
}

/** MUST match backend bulkMatchCore.BLOCK_NO_QTY verbatim — the only block the
 *  wizard can clear inline (by entering a quantity). */
export const BLOCK_NO_QTY = 'no quantity — enter one to accept';
